"use strict";

import Config from './Config';

export const MigrationStatusValue = {
    NOT_STARTED: 'NOT_STARTED',
    PENDING: 'PENDING',
    COMPLETED: 'COMPLETED',
    FAILED: 'FAILED'
};

/** FailCount at or above this shows the multi-failure view (PDF Web 11). */
export const MIGRATION_MULTIPLE_FAILURE_THRESHOLD = 2;

export const MigrationPageView = {
    FORM: 'FORM',
    COMPLETE: 'COMPLETE',
    IN_PROGRESS: 'IN_PROGRESS',
    MULTI_FAILED: 'MULTI_FAILED',
};

/**
 * Which inline view to show on the account migration page.
 *
 * @param {string} migrationStatus
 * @param {number} failCount
 * @return {string}
 */
export function getMigrationPageView(migrationStatus, failCount = 0) {
    switch (migrationStatus) {
        case MigrationStatusValue.COMPLETED:
            return MigrationPageView.COMPLETE;
        case MigrationStatusValue.PENDING:
            return MigrationPageView.IN_PROGRESS;
        case MigrationStatusValue.FAILED:
        case MigrationStatusValue.NOT_STARTED:
            if (failCount >= MIGRATION_MULTIPLE_FAILURE_THRESHOLD) {
                return MigrationPageView.MULTI_FAILED;
            }
            return MigrationPageView.FORM;
        default:          
            return MigrationPageView.FORM;
    }
}

/**
 * Migration API failures arrive as an APIRequestError carrying
 * `error_code_<subcode>` as its message (see types/ExtendableError), so the
 * subcode the customer is asked to quote has to be read back out of it.
 *
 * @param {?Object} error MigrationStore error
 * @return {?string}
 */
export function getMigrationErrorCode(error) {
    const message = error && error.message ? String(error.message) : '';
    const match = message.match(/^error_code_(\w+)$/);
    return match ? match[1] : null;
}

/**
 * Only an absolute http(s) URL can be navigated to. A relative one resolves
 * against the storefront origin, where the response is JSON rather than a page,
 * so the browser offers it as a file download instead of leaving the site.
 *
 * @param {?string} url
 * @return {boolean}
 */
export function isExternalRedirectUrl(url) {
    return /^https?:\/\//i.test(url || '');
}

/**
 * localStorage key holding the Rakuten TV hand-off URL for one user. Keyed by
 * user so a shared browser cannot send the wrong person to a URL that may carry
 * a token, matching the `<userName>_pending` convention in migrationPolling.
 *
 * @param {?string} userName
 * @return {?string}
 */
export function getMigrationRedirectStorageKey(userName) {
    return userName ? `${userName}_migration_redirect` : null;
}

/**
 * MigrationStore only holds the hand-off URL for the request that returned it,
 * but Web 9's exit can happen sessions later, so it has to be persisted.
 *
 * @param {?string} userName
 * @param {?string} url
 */
export function setStoredMigrationRedirectUrl(userName, url) {
    const key = getMigrationRedirectStorageKey(userName);
    if (!key || !url) {
        return;
    }
    try {
        localStorage.setItem(key, url);
    } catch (error) {
        // Private browsing and exhausted quotas both throw; the caller's
        // fallback URL covers it.
    }
}

/**
 * Anything unusable is reported as absent so the caller falls back, which also
 * discards values persisted before the URL was validated on the way in.
 *
 * @param {?string} userName
 * @return {?string}
 */
export function getStoredMigrationRedirectUrl(userName) {
    const key = getMigrationRedirectStorageKey(userName);
    if (!key) {
        return null;
    }
    try {
        const url = localStorage.getItem(key);
        return isExternalRedirectUrl(url) ? url : null;
    } catch (error) {
        return null;
    }
}

/**
 * Whether the user can submit a new migration request from the migration page.
 *
 * @param {string} migrationStatus
 * @return {boolean}
 */
export function canRequestMigration(migrationStatus) {
    return migrationStatus === MigrationStatusValue.NOT_STARTED
        || migrationStatus === MigrationStatusValue.FAILED;
}

/**
 * Whether the signed-in user has finished migration to Rakuten TV.
 *
 * @param {?Object} migrationStatusRecord MigrationStore migrationStatus record
 * @return {boolean}
 */
export function isMigrationCompleted(migrationStatusRecord) {
    return !!(migrationStatusRecord
        && migrationStatusRecord.migrationStatus === MigrationStatusValue.COMPLETED);
}

/**
 * Whether migration status blocks Virgin Media Store access (in progress or complete).
 *
 * @param {?Object} migrationStatusRecord MigrationStore migrationStatus record
 * @return {boolean}
 */
export function isMigrationBlockingStoreAccess(migrationStatusRecord) {
    if (!migrationStatusRecord) {
        return false;
    }
    const status = migrationStatusRecord.migrationStatus;
    return status === MigrationStatusValue.PENDING
        || status === MigrationStatusValue.COMPLETED;
}

/**
 * Whether lockdown rules apply for the current migration status.
 *
 * @param {?Object} migrationStatusRecord MigrationStore migrationStatus record
 * @return {boolean}
 */
export function shouldApplyMigrationLockdown(migrationStatusRecord) {
    return isMigrationBlockingStoreAccess(migrationStatusRecord);
}

/**
 * @param {string} pathname
 * @return {boolean}
 */
export function isOnMigrationPath(pathname) {
    return matchesPath(pathname, Config.ROOT + 'migrate');
}

/**
 * @param {string} pathname
 * @return {boolean}
 */
export function isOnAccountPath(pathname) {
    return matchesPath(pathname, Config.ROOT + 'update-account');
}

/**
 * @param {string} pathname
 * @return {boolean}
 */
export function isOnMigrationTermsPath(pathname) {
    return matchesPath(pathname, Config.ROOT + 'migration-terms-and-conditions');
}

/**
 * Paths a locked down user (migration PENDING or COMPLETED) may still open.
 *
 * @param {string} pathname
 * @return {boolean}
 */
export function isMigrationLockdownAllowedPath(pathname) {
    return isOnMigrationPath(pathname)
        || isOnAccountPath(pathname)
        || isOnMigrationTermsPath(pathname);
}

/**
 * @param {string} pathname
 * @param {string} path
 * @return {boolean}
 */
function matchesPath(pathname, path) {
    return pathname === path || pathname.indexOf(path) === 0;
}
